import React, { useState, memo, useCallback, useMemo } from 'react';
import { Handle, Position, NodeProps, useReactFlow } from 'reactflow';
import Icon from '../forms/icon.component';
import { AlertService } from '../../services/alert.service';
import { BotsService } from '../../services/bots.service';
import DefaultBody from './flows-body/default-body.component';
import HttpBody from './flows-body/http-body.component';
import InteractiveBody from './flows-body/interactive-body.component';
import RedirectBody from './flows-body/redirect-body.component';
import CloseBody from './flows-body/close-body.component';
import { NodeData } from '../../pages/campaings/flows/types';

type NodeIcons = Record<NodeData['type'], string>;

const NODE_ICONS: NodeIcons = {
  DEFAULT: 'ki-note',
  INTERACTIVE: 'ki-technology-4',
  HTTP: 'ki-router',
  REDIRECT_AGENT: 'ki-user-square',
  CLOSE: 'ki-entrance-left'
};

const BODY_COMPONENTS = {
  DEFAULT: DefaultBody,
  HTTP: HttpBody,
  INTERACTIVE: InteractiveBody,
  REDIRECT_AGENT: RedirectBody,
  CLOSE: CloseBody
} as const;

const NodeType: React.FC<NodeProps<NodeData>> = (props) => {
  const { data, id, selected } = props;
  const { setNodes, setEdges, getNodes, getEdges } = useReactFlow();
  const nodeClass = data.isMain ? 'is-main' : 'is-secondary';
  const [showMenu, setShowMenu] = useState(false);
  const showRightHandle = !['INTERACTIVE', 'HTTP', 'CLOSE'].includes(data.type);

  const handleDelete = useCallback(() => {
    AlertService.warning(
      `¡ Atención !, estás a punto de eliminar el nodo ${data.title}. ¿Estás seguro?`,
      `Eliminar nodo ${data.title}`,
      async () => {
        if (data.meta?.isNew) {
          setNodes((nds) => nds.filter((n) => n.id !== id));
          setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
          setShowMenu(false);
          AlertService.toastSuccess('Se ha eliminado el nodo');
          return;
        }
        try {
          await BotsService.deleteNode(id);
          setNodes((nds) => nds.filter((n) => n.id !== id));
          setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
          setShowMenu(false);
          AlertService.toastSuccess('Se ha eliminado el nodo');
        } catch (err: any) {
          console.log('Error: ', err);
          AlertService.toastError(err.error || err);
        }
      },
      'Eliminar'
    );
  }, [id]);

  const handleMakeMain = useCallback(() => {
    AlertService.warning(
      `Vas a convertir este nodo en principal:\n"${data.title}"`,
      'Confirmar nodo principal',
      async () => {
        try {
          await BotsService.setNodeRoot({ _id: id });
          setNodes((nds) =>
            nds.map((n) => ({
              ...n,
              data: { ...n.data, isMain: n.id === id }
            }))
          );
          const currentEdges = getEdges();
          const incoming = currentEdges.filter((e) => e.target === id);
          const allNodes = getNodes();
          for (const edge of incoming) {
            const { source, target, sourceHandle } = edge;
            const srcNode = allNodes.find((n) => n.id === source);
            const keywords =
              srcNode && ['HTTP', 'INTERACTIVE'].includes(srcNode.data.type) ? [sourceHandle] : [];

            try {
              await BotsService.updateEdges({
                action: 'DELETE',
                source,
                target,
                keywords
              });
            } catch (err: any) {
              console.error('Error al eliminar arista ', err);
              AlertService.toastError(err.error || err);
            }
          }
          setEdges((eds) => eds.filter((e) => e.target !== id));
          AlertService.toastSuccess('Nodo marcado como principal');
        } catch (err: any) {
          AlertService.toastError(err.error || err);
        }
      },
      'Sí, hacerlo'
    );
    setShowMenu(false);
  }, [data.title, id, setNodes]);

  const handleClickOutside = useCallback(() => {
    setShowMenu(false);
  }, []);

  const BodyComponent = useMemo(() => BODY_COMPONENTS[data.type], [data.type]);
  return (
    <div className={`default-node ${nodeClass}` + (selected ? ' shadow-lg' : '')}>
      <div className="node-color-bar">
        <Icon
          icon={NODE_ICONS[data.type]}
          size="fs-2"
          className="hover-icon"
          type={data.isMain ? 'primary' : 'info'}
        />
      </div>

      <div className="node-content ps-2">
        {/* Header */}
        <div className="node-header d-flex align-items-center justify-content-between">
          <h5 className="node-title mb-0 d-flex align-items-center">
            <span className="text-truncate">{data?.title || 'sin título'}</span>
            {data.isMain && (
              <Icon
                icon="ki-star"
                size="fs-2"
                className="hover-icon ms-2 flex-shrink-0"
                type="warning"
              />
            )}
          </h5>
          <div className="node-menu position-relative">
            <i
              className="bi bi-three-dots-vertical hover-icon"
              onClick={() => setShowMenu(!showMenu)}
            ></i>
            {showMenu && (
              <>
                <div
                  className="menu-desplegable animate-fadeInUp animate-delay-1 shadow-sm fs-10"
                  style={{ zIndex: 10 }}
                  onClick={handleClickOutside}
                >
                  {!data.isMain && !data.meta?.isNew && (
                    <div className="menu-item">
                      <button
                        className="btn btn-sm menu-link w-150px text-start"
                        onClick={handleMakeMain}
                      >
                        <Icon icon="ki-star" size="fs-1" type="warning" />
                        <span>Hacer principal</span>
                      </button>
                    </div>
                  )}
                  <div className="menu-item pt-0">
                    <button
                      className="btn btn-sm menu-link w-150px h-35px text-start"
                      type="button"
                      onClick={handleDelete}
                    >
                      <Icon icon="ki-trash" size="fs-1" type="danger" />
                      <span>Eliminar nodo</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Body */}
        <div className="node-body pe-2 text-start">
          {data.meta?.isNew ? (
            <div className="badge badge-light-danger">Sin configurar </div>
          ) : (
            <BodyComponent data={data} />
          )}
        </div>
      </div>

      {/* Handles */}
      {!data.isMain && (
        <Handle type="target" position={Position.Left} className="handle-connector" />
      )}
      {showRightHandle && (
        <Handle type="source" position={Position.Right} className="handle-connector" />
      )}
    </div>
  );
};

export default memo(NodeType);
